// Libs
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useParams } from 'react-router-dom';

// Actions
import { AddToMyKits, CleanKitActionErrorMessage } from '../../../actions/kits';
import { OpenModal } from '../../../actions/modal';
import { Open2Modal, Close2Modal } from '../../../actions/modal2';

// Components
import KitNameModal from '../../../components/Kits/Modal/KitNameModal/KitNameModal';
import OrderConfirmation from '../../../components/OrderHistory/Modals/OrderConfirmation/OrderConfirmation';

// Hooks
import useModal from '../../../hooks/useModal';
import useUI from '../../../hooks/useUI';
import useSharedKitMessages from '../../../hooks/useSharedKitMessages';

// Utils
import { splitOrderItems } from '../utils/sharedKitOrderItems';

// Selectors
import { productsObjSelector } from '../../../store/selectors';

// Styles
import { colors } from '../../../styles/palette';

// Constants
import {
  FORM_MODAL_HEIGHT_DESKTOP,
  FORM_MODAL_HEIGHT_MOBILE,
  FORM_MODAL_WIDTH_DESKTOP,
  FORM_MODAL_WIDTH_MOBILE,
  FORM_MODAL_WIDTH_TABLET,
  NOTIFICATION_MODAL_CLOSE_DELAY_MS,
} from '../../../constants/modals';

const useAddToKits = (orderId) => {
  const dispatch = useDispatch();
  const timerRef = useRef(null);
  const { orders, currentOrder, placedOrder, productsObj } = useSelector(
    (state) => ({
      orders: state.orders.general,
      currentOrder: state.orders.currentOrder,
      placedOrder: state.cart?.placedOrder,
      productsObj: productsObjSelector(state),
    }),
  );
  const { onClose } = useModal();
  const { pathname } = useLocation();
  const params = useParams();
  const { isTabletMode, isMobileMode } = useUI();
  const { addToMyKitsMessage } = useSharedKitMessages();

  const orderItems = useMemo(() => {
    if (pathname.includes('order-history')) {
      return params.orderId
        ? currentOrder.order_items
        : orders.find((order) => order.id === orderId)?.order_items;
    }

    return placedOrder?.order_items?.map((item) => ({
      ...item,
      product: item.product?.product_id ?? item.product,
    }));
  }, [pathname, params, orderId, currentOrder, orders, placedOrder]);

  const unavailableProducts = useMemo(
    () =>
      orderItems?.filter(
        ({ product }) => !productsObj?.[product]?.is_available_for_user,
      ),
    [orderItems, productsObj],
  );

  const sharedKitOnlyProducts = useMemo(
    () => splitOrderItems(orderItems || [], productsObj).sharedKitOnly,
    [orderItems, productsObj],
  );

  const showNotificationModal = useCallback(() => {
    dispatch(
      Open2Modal({
        id: 'added-to-kits',
        Component: () => <Typography variant="h3">Added To My Kits</Typography>,
        closeIcon: false,
        styles: {
          width: 'max-content',
          minHeight: 'initial',
          color: colors.darkGrey,
        },
        props: {
          transitionTimeout: { enter: 0, exit: 500 },
          paddingContent: {
            xl: '20px',
            lg: '20px',
            md: '20px',
          },
        },
      }),
    );
    timerRef.current = setTimeout(
      () => dispatch(Close2Modal()),
      NOTIFICATION_MODAL_CLOSE_DELAY_MS,
    );
  }, [dispatch]);

  useEffect(() => {
    return () => clearTimeout(timerRef?.current);
  }, [dispatch]);

  const onClickSave = useCallback(
    (orderId, name) => {
      dispatch(
        AddToMyKits({
          orderId,
          name,
          callback: showNotificationModal,
        }),
      );
    },
    [dispatch, showNotificationModal],
  );

  const cleanUp = useCallback(() => {
    dispatch(CleanKitActionErrorMessage());
  }, [dispatch]);

  const showWindowWithKitName = useCallback(() => {
    let width;
    if (isMobileMode) {
      width = FORM_MODAL_WIDTH_MOBILE;
    } else if (isTabletMode) {
      width = FORM_MODAL_WIDTH_TABLET;
    } else {
      width = FORM_MODAL_WIDTH_DESKTOP;
    }

    dispatch(
      OpenModal({
        Component: KitNameModal,
        props: {
          entityId: orderId,
          onClickSave,
          onClickCancel: onClose,
          cleanUp,
          paddingContent: {
            xl: '40px 40px 30px',
            lg: '40px 40px 30px',
            md: '24px 16px 24px',
          },
        },
        styles: {
          width,
          height: isMobileMode
            ? FORM_MODAL_HEIGHT_MOBILE
            : FORM_MODAL_HEIGHT_DESKTOP,
        },
      }),
    );
  }, [
    dispatch,
    orderId,
    onClose,
    onClickSave,
    cleanUp,
    isMobileMode,
    isTabletMode,
  ]);

  const showConfirmation = useCallback(
    ({ products, header, onConfirm }) => {
      dispatch(
        OpenModal({
          Component: OrderConfirmation,
          props: {
            unavailableProducts: products.map(
              ({ product }) => productsObj[product],
            ),
            header,
            description:
              products.length === 1
                ? 'Do You Want To Create Kit Without This Product? '
                : 'Do You Want To Create Kit Without These Products? ',
            cancelAction: onClose,
            confirmAction: onConfirm,
          },
          styles: {
            width: 'min(90vw, 651px)',
          },
        }),
      );
    },
    [dispatch, productsObj, onClose],
  );

  const onlyUnavailableProducts = useMemo(
    () =>
      (unavailableProducts || []).filter(
        (item) => !sharedKitOnlyProducts.includes(item),
      ),
    [unavailableProducts, sharedKitOnlyProducts],
  );

  const showWindowWithUnavailableProducts = useCallback(() => {
    showConfirmation({
      products: onlyUnavailableProducts,
      header:
        onlyUnavailableProducts.length === 1
          ? 'This Product Is No Longer Available.'
          : 'These Products Are No Longer Available.',
      onConfirm: showWindowWithKitName,
    });
  }, [showConfirmation, onlyUnavailableProducts, showWindowWithKitName]);

  const onAddToMyKits = useCallback(() => {
    if (sharedKitOnlyProducts.length) {
      showConfirmation({
        products: sharedKitOnlyProducts,
        header: addToMyKitsMessage,
        onConfirm: onlyUnavailableProducts.length
          ? showWindowWithUnavailableProducts
          : showWindowWithKitName,
      });
      return;
    }

    if (onlyUnavailableProducts.length) {
      showWindowWithUnavailableProducts();
      return;
    }

    showWindowWithKitName();
  }, [
    showConfirmation,
    sharedKitOnlyProducts,
    onlyUnavailableProducts,
    addToMyKitsMessage,
    showWindowWithUnavailableProducts,
    showWindowWithKitName,
  ]);

  return { onAddToMyKits };
};

export default useAddToKits;
